import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthenticationModel } from '@models'
import {
  ONE_MOMENT,
  PROCESSING_SECURE_TRANSACTION
} from '@constants/translationsKeys'
import { AuthenticationProvider, AuthStep } from '@enums'
import { getAuthenticationStatusString } from '@utils/authentication-helpers'
import { Loading, Result } from '@components'
import { NotificationForm } from '@forms'
import { useAuthProcess } from '../../hooks'
import { ChallengeIframe } from './components'

const {
  P3F_INSWITCH_CHECK_AUTH_RESULT_DELAY_MS: INSWITCH_CHECK_AUTH_RESULT_DELAY_MS,
  P3F_INSWITCH_SEND_NOTIFICATION_DELAY_MS: INSWITCH_SEND_NOTIFICATION_DELAY_MS
} = p3fenv

interface Props {
  showBrand: boolean
  initialAuthentication?: AuthenticationModel
}

const InswitchAuth = memo<Props>(({ showBrand, initialAuthentication }) => {
  const [isIframeVisible, setIsIframeVisible] = useState<boolean>(false)

  const { t } = useTranslation()
  const {
    isLoading,
    authStep,
    authentication,
    setupData,
    checkAuthenticationResult
  } = useAuthProcess({
    authProvider: AuthenticationProvider.INSWITCH,
    initialAuthentication,
    showBrand,
    checkAuthResultDelay: INSWITCH_CHECK_AUTH_RESULT_DELAY_MS,
    sendNotificationDelay: INSWITCH_SEND_NOTIFICATION_DELAY_MS
  })

  const renderLoading = () => (
    <Loading
      showBrand={showBrand}
      message={t(ONE_MOMENT, { extra: '...' })}
      description={t(PROCESSING_SECURE_TRANSACTION, { extra: '...' })}
    />
  )

  const renderStepUp = () => (
    <div className="relative flex flex-col">
      {!isIframeVisible && renderLoading()}
      <ChallengeIframe
        collectionURL={setupData?.collectionUrl ?? ''}
        checkAuthenticationResult={checkAuthenticationResult}
        isVisible={isIframeVisible}
        changeIframeVisibility={(isVisible) => {
          setIsIframeVisible(isVisible)
        }}
      />
    </div>
  )

  const renderNotification = () => (
    <>
      {renderLoading()}
      <NotificationForm
        status={getAuthenticationStatusString(authentication?.status)}
        message={authentication?.statusMsg ?? ''}
        authenticationId={authentication?.authenticationId ?? ''}
        orderId={authentication?.orderId ?? ''}
        orderAmount={authentication?.orderAmount ?? 0}
        redirectUrl={authentication?.externalReturnUrl ?? ''}
      />
    </>
  )

  const renderResult = () => (
    <Result
      authStatus={authentication?.status}
      showBrand={showBrand}
      message={authentication?.statusMsg ?? ''}
    />
  )

  if (isLoading) return renderLoading()

  const stepComponents: Partial<Record<AuthStep, () => JSX.Element>> = {
    [AuthStep.STEP_UP]: renderStepUp,
    [AuthStep.NOTIFICATION]: renderNotification,
    [AuthStep.RESULT]: renderResult
  }

  return stepComponents[authStep]?.() ?? renderLoading()
})

export default InswitchAuth
